import React from 'react';
import Banner from '../common/Banner';
import { useSubscription } from '../../hooks/useSubscription';
import { useCanPerform } from '../../hooks/useCanPerform';
import { openOrganizationAccount } from '../../constants/navigationEvents';
import { CAP_WARNING_PCT, capPercent, planLabel } from '../../utils/subscription';
import type { AccountSubscription } from '../../types/permissions';

/** Cap usage when the banner applies (≥80% of a fixed cap), else null. */
export function capBannerUsage(subscription: AccountSubscription | null | undefined): { used: number; limit: number; pct: number } | null {
  if (!subscription) return null;
  const { used, limit } = subscription.cap;
  if (limit === null) return null;
  const pct = capPercent(used, limit);
  if (pct === null || pct < CAP_WARNING_PCT) return null;
  return { used, limit, pct };
}

interface CapUsageBannerProps {
  className?: string;
}

/**
 * Patient cap notice (Phase 13, ACC-10): "{used} of {limit} patients on {Plan}" once 80% of the
 * cap is in use; at the cap it turns into a warning. Managers get "View plans".
 */
const CapUsageBanner: React.FC<CapUsageBannerProps> = ({ className = '' }) => {
  const subscription = useSubscription();
  const canManage = useCanPerform('manage:subscription');
  const usage = capBannerUsage(subscription);
  if (!subscription || !usage) return null;
  const atCap = usage.used >= usage.limit;
  return (
    <Banner
      variant={atCap ? 'warning' : 'info'}
      data-testid="cap-usage-banner"
      className={className}
      action={canManage ? (
        <button
          type="button"
          onClick={openOrganizationAccount}
          className="rounded border border-current bg-white px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
        >
          View plans
        </button>
      ) : undefined}
    >
      {usage.used} of {usage.limit} patients on {planLabel(subscription.plan)}.
      {atCap ? ' Upgrade to admit more patients.' : ' Referrals and discharged patients do not count.'}
    </Banner>
  );
};

export default CapUsageBanner;
